import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, clearAdminToken } from '../../services/adminApi';
import { Loader2, ArrowLeft, Search, Bot } from 'lucide-react';

const PAGE = 50;
const OUTCOME = {
  answered: { label: 'Answered', cls: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
  no_records: { label: 'No records', cls: 'text-sky-300 border-sky-500/30 bg-sky-500/10' },
  not_found: { label: 'Data gap', cls: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
  error: { label: 'Error', cls: 'text-red-400 border-red-500/30 bg-red-500/10' },
};

const Metric = ({ label, value, testid }) => (
  <div className="rounded-md border border-border bg-card p-4" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="text-2xl font-bold mt-1 font-mono">{value}</p>
  </div>
);

const LogRow = ({ r, open, onToggle }) => {
  const o = OUTCOME[r.outcome] || OUTCOME.error;
  return (
    <>
      <tr className="border-t border-border hover:bg-muted/20 cursor-pointer" onClick={onToggle} data-testid={`ai-log-row-${r.id}`}>
        <td className="p-3 font-mono text-xs whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
        <td className="p-3">{r.tenant_name || r.tenant_id?.slice(0, 8)}<div className="text-xs text-muted-foreground">{r.user_email}</div></td>
        <td className="p-3 max-w-md">
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mr-2" data-testid={`ai-log-channel-${r.id}`}>{r.channel === 'guardian_help_ai' ? 'Help' : 'Chat'}</span>
          <span className="truncate">{r.question}</span>
        </td>
        <td className="p-3"><span className={`text-[10px] font-mono uppercase tracking-widest border rounded-sm px-1.5 py-0.5 ${o.cls}`}>{o.label}</span></td>
        <td className="p-3 font-mono text-xs">{r.record_count ?? 0}</td>
        <td className="p-3 font-mono text-xs">{(r.input_tokens || 0)} / {(r.output_tokens || 0)}</td>
        <td className="p-3 font-mono text-xs">{r.latency_ms ?? '—'} ms</td>
      </tr>
      {open && (
        <tr className="bg-muted/10" data-testid={`ai-log-detail-${r.id}`}>
          <td colSpan={7} className="p-4 text-sm space-y-2">
            <div><span className="text-muted-foreground">Answer: </span>{r.final_answer || '—'}</div>
            {r.error && <div className="text-red-400 font-mono text-xs">Error ({r.error_ref}): {r.error}</div>}
            <div className="text-xs text-muted-foreground">
              Session {r.session_id} · {r.context_type}{r.context_id ? ` ${r.context_id}` : ''} · model {r.model || 'not called'}
            </div>
            {(r.records_used || []).length > 0 && (
              <ul className="text-xs list-disc pl-5">
                {r.records_used.map((t, i) => <li key={i}>{t.scope}: <span className="font-mono">{t.shown != null && t.shown < t.count ? `showing ${t.shown} of ${t.count}` : t.count}</span></li>)}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const AdminAiChatLogsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, summary: {}, tenants: [] });
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState('');
  const [outcome, setOutcome] = useState('');
  const [channel, setChannel] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const r = await adminApi.aiChatLogs({ tenant_id: tenant || undefined, channel: channel || undefined, outcome: outcome || undefined, q: q || undefined, limit: PAGE, offset });
      setData(prev => offset ? { ...r.data, items: [...prev.items, ...r.data.items] } : r.data);
    } catch (e) {
      if (e.response?.status === 401) { clearAdminToken(); navigate('/admin/login', { replace: true }); }
    } finally { setLoading(false); }
  }, [tenant, channel, outcome, q, navigate]);

  useEffect(() => { const t = setTimeout(() => load(0), 250); return () => clearTimeout(t); }, [load]);
  const s = data.summary || {};

  return (
    <div className="min-h-screen bg-background" data-testid="admin-ai-logs-page">
      <header className="border-b border-border bg-card/40 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button onClick={() => navigate('/admin/tenants')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="admin-back-to-tenants">
            <ArrowLeft className="w-4 h-4" /> Tenants
          </button>
          <h1 className="text-base font-bold uppercase tracking-tight flex items-center gap-2"><Bot className="w-4 h-4 text-primary" />Guardian AI log</h1>
          <div />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Metric label="Questions" value={s.questions ?? 0} testid="ai-log-stat-questions" />
          <Metric label="Data gaps" value={s.gaps ?? 0} testid="ai-log-stat-gaps" />
          <Metric label="Errors" value={s.errors ?? 0} testid="ai-log-stat-errors" />
          <Metric label="Tokens in / out" value={`${s.input_tokens ?? 0} / ${s.output_tokens ?? 0}`} testid="ai-log-stat-tokens" />
          <Metric label="Avg latency" value={`${Math.round(s.avg_latency_ms || 0)} ms`} testid="ai-log-stat-latency" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions…" className="w-full h-9 pl-9 pr-3 rounded-md bg-card border border-border text-sm" data-testid="ai-log-search" />
          </div>
          <select value={tenant} onChange={(e) => setTenant(e.target.value)} className="h-9 px-3 rounded-md bg-card border border-border text-sm" data-testid="ai-log-tenant-filter">
            <option value="">All companies</option>
            {(data.tenants || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select value={channel} onChange={(e) => setChannel(e.target.value)} className="h-9 px-3 rounded-md bg-card border border-border text-sm" data-testid="ai-log-channel-filter">
            <option value="">Chat and Help</option>
            <option value="guardian_web_chat">Chat</option>
            <option value="guardian_help_ai">Help</option>
          </select>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="h-9 px-3 rounded-md bg-card border border-border text-sm" data-testid="ai-log-outcome-filter">
            <option value="">Any outcome</option>
            <option value="answered">Answered</option>
            <option value="no_records">No records</option>
            <option value="not_found">Data gaps</option>
            <option value="error">Errors</option>
          </select>
          <div className="ml-auto text-xs text-muted-foreground" data-testid="ai-log-count">{data.total} question{data.total === 1 ? '' : 's'}</div>
        </div>

        <div className="rounded-md border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left p-3">When</th><th className="text-left p-3">Company / user</th><th className="text-left p-3">Question</th>
                <th className="text-left p-3">Outcome</th><th className="text-left p-3">Records</th><th className="text-left p-3">Tokens in/out</th><th className="text-left p-3">Latency</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(r => <LogRow key={r.id} r={r} open={openId === r.id} onToggle={() => setOpenId(openId === r.id ? null : r.id)} />)}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={7} className="p-12 text-center text-muted-foreground" data-testid="ai-log-empty">No questions logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {loading && <div className="flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
        {!loading && data.items.length < data.total && (
          <div className="flex justify-center">
            <button onClick={() => load(data.items.length)} className="text-xs px-3 py-1.5 rounded-sm border border-border hover:bg-secondary" data-testid="ai-log-load-more">Load more</button>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminAiChatLogsPage;
